import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { log } from "../lib/log";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Share2 } from "lucide-react";
import { toast } from "sonner";
import StardustBurst from "../components/StardustBurst";
import PaywallModal from "../components/PaywallModal";
import WorldMap from "../components/WorldMap";
import ShareCardModal from "../components/ShareCardModal";
import TopCitiesRow from "../components/TopCitiesRow";
import { WellnessPlannerModal } from "../components/WellnessPlannerModal";
import { ScoreOrb, ScarcityBanner, WildcardCard, JourneyCTA } from "../components/scanner/ScanResultCards";
import { FacetBreakdown } from "../components/scanner/FacetBreakdown";
import { BaziGuide } from "../components/scanner/BaziGuide";
import { ScanBackdrop } from "../components/scanner/ScanBackdrop";
import { prefetchWorldPaths } from "../components/worldmap/useWorldPaths";

export default function Scanner() {
  const { user, refresh } = useAuth();
  const [city, setCity] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [burst, setBurst] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [previousPins, setPreviousPins] = useState([]);
  const [astroLines, setAstroLines] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [wellnessOpen, setWellnessOpen] = useState(false);
  const [wellnessCity, setWellnessCity] = useState(null);
  const [wellnessRun, setWellnessRun] = useState(0);
  const openWellness = (city) => { setWellnessCity(city); setWellnessRun((n) => n + 1); setWellnessOpen(true); };

  useEffect(() => {
    prefetchWorldPaths();
    (async () => {
      try {
        const [scans, astro] = await Promise.all([
          api.get("/scans"),
          api.get("/users/astrocartography").catch(() => ({ data: { lines: [] } })),
        ]);
        setPreviousPins(scans.data.filter((s) => s.lat != null && s.lng != null).slice(0, 10));
        setAstroLines(astro.data.lines || []);
      } catch (e) {
        log.warn("Scanner: initial /scans + /astrocartography fetch failed:", e);
      }
    })();
  }, []);

  const runScan = async (target) => {
    const q = (target ?? city).trim();
    if (!q) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await api.post("/scan", { city: q });
      setResult(res.data);
      if (res.data.easter_egg) setBurst(true);
      refresh();
    } catch (err) {
      if (err?.response?.status === 402) setPaywallOpen(true);
      else toast.error(err?.response?.data?.detail || "Scanner offline");
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e) => { e.preventDefault(); runScan(); };
  const scanWildcard = (cityDisplay) => { setCity(cityDisplay); runScan(cityDisplay); };

  return (
    <div className="pt-6 relative z-10">
      <ScanBackdrop city={result?.city} />
      <p className="text-xs uppercase tracking-[0.3em] text-[#A89C98]">Destiny Scanner</p>
      <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter mt-2" data-testid="scanner-title">Type a city. Read the resonance.</h1>

      <form onSubmit={onSubmit} className="mt-10 flex gap-3 items-end" data-testid="scanner-form">
        <div className="flex-1">
          <label className="text-xs uppercase tracking-[0.2em] text-[#A89C98]">Coordinate Input</label>
          <Input
            data-testid="scanner-city-input"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Lisbon, Tokyo, Reykjavík…"
            className="mt-2 bg-transparent border-0 border-b border-[#A89C98]/30 rounded-none px-0 text-white text-xl focus-visible:ring-0 focus-visible:border-[#C8A96E] h-12"
          />
        </div>
        <Button data-testid="scanner-scan-btn" type="submit" disabled={busy} className="bg-[#C8A96E] text-[#15111A] hover:bg-[#A98B55] rounded-none h-12 px-8 uppercase tracking-widest">
          {busy ? "Scanning…" : "Scan"}
        </Button>
      </form>

      <ScoreOrb result={result} />
      <ScarcityBanner scarcity={result?.scarcity} />
      <WildcardCard wildcard={result?.wildcard} onScan={scanWildcard} />
      {result && !result.easter_egg && <JourneyCTA city={result.city} onOpen={() => openWellness(null)} onPackages={() => openWellness(result.city)} />}
      {result && <FacetBreakdown score={result.score} facets={result.facets} topFacet={result.top_facet} locked={result.insights_locked} city={result.city} />}
      {result && result.whole_guide && !result.whole_guide.locked && <BaziGuide guide={result.whole_guide} />}

      {result && (
        <div className="mt-8 flex justify-center">
          <Button data-testid="scanner-share-postcard-btn" onClick={() => setShareOpen(true)} className="bg-transparent border border-[#C8A96E] text-[#C8A96E] hover:bg-[#C8A96E] hover:text-[#15111A] rounded-none h-11 px-6 uppercase tracking-widest text-xs font-bold flex items-center gap-2">
            <Share2 size={15} /> Share Blueprint Postcard
          </Button>
        </div>
      )}

      {result && result.lat != null && result.lng != null && (
        <div className="mt-10 space-y-6">
          {user?.natal && <TopCitiesRow />}
          <WorldMap
            lat={result.lat}
            lng={result.lng}
            city={result.city}
            score={result.score}
            easterEgg={result.easter_egg}
            previousPins={previousPins.filter((p) => p.city.toLowerCase() !== result.city.toLowerCase())}
            astroLines={astroLines}
          />
        </div>
      )}

      {burst && (
        <StardustBurst onClose={() => setBurst(false)} score={result?.score} zodiac={user?.natal?.zodiac} personalizedTag={user?.natal?.personalized_tag} />
      )}
      <PaywallModal open={paywallOpen} onClose={() => { setPaywallOpen(false); refresh(); }} />
      <ShareCardModal open={shareOpen} onClose={() => setShareOpen(false)} />
      <WellnessPlannerModal key={wellnessRun} open={wellnessOpen} onClose={() => setWellnessOpen(false)} destination={wellnessCity} />
    </div>
  );
}
